import { useRef, useMemo } from 'react'

import { ISideNavProps } from './SideNav.types'
import * as S from './SideNav.styles'
import { getNavItemsWithPermissions } from './SideNav.data'

import { Badge } from '@/components/ui/Badge'
import { usePermissions } from '@/features/authorization/hooks/usePermissions/usePermissions'

export const SideNav = ({
    userType,
    currentItem,
    onNavigate,
    isExpanded,
    onHover,
    isMobile = false,
}: ISideNavProps) => {
    const { user } = usePermissions()
    const hoverTimeoutRef = useRef<number | null>(null)
    const leaveTimeoutRef = useRef<number | null>(null)

    // Memoize nav items to prevent unnecessary recalculation on every render
    const navItems = useMemo(() => {
        const userPermissions = user?.role?.permissions || []
        return getNavItemsWithPermissions(userType, userPermissions)
    }, [userType, user?.role?.permissions])

    const handleMouseEnter = () => {
        // Disable hover behavior on mobile
        if (isMobile || !onHover) return

        // Clear any existing leave timeout
        if (leaveTimeoutRef.current) {
            clearTimeout(leaveTimeoutRef.current)
            leaveTimeoutRef.current = null
        }

        // Set a delay before expanding - like Diversio Frontend
        hoverTimeoutRef.current = window.setTimeout(() => {
            onHover(true)
        }, 300) // 300ms delay for better UX
    }

    const handleMouseLeave = () => {
        // Disable hover behavior on mobile
        if (isMobile || !onHover) return

        // Clear any existing enter timeout
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current)
            hoverTimeoutRef.current = null
        }

        // Set a delay before collapsing - like Diversio Frontend
        leaveTimeoutRef.current = window.setTimeout(() => {
            onHover(false)
        }, 150) // 150ms delay for smooth collapse, synced with 250ms transition
    }

    return (
        <S.SideNavContainer
            $isExpanded={isExpanded}
            $isMobile={isMobile}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <S.Header $isMobile={isMobile}></S.Header>

            {/* Navigation Items */}
            <S.NavigationContainer>
                {navItems.map((item) => (
                    <S.NavItemWrapper key={item.id} $isExpanded={isExpanded}>
                        <S.NavButton $isActive={currentItem === item.id} onClick={() => onNavigate(item.id)}>
                            <S.NavButtonContent $isExpanded={isExpanded}>
                                <S.IconWrapper>
                                    <item.icon />
                                    {item.badge && !isExpanded && (
                                        <S.NotificationDot>
                                            <S.NotificationNumber>{item.badge}</S.NotificationNumber>
                                        </S.NotificationDot>
                                    )}
                                </S.IconWrapper>

                                <S.NavContentWrapper $isExpanded={isExpanded}>
                                    {isExpanded && (
                                        <S.NavTextContent>
                                            <S.NavItemHeader>
                                                <S.NavLabel>{item.label}</S.NavLabel>
                                                {item.badge && (
                                                    <Badge
                                                        variant="secondary"
                                                        size="small"
                                                        className="" /* h-4 px-1.5 text-xs bg-white/20 text-white border-white/30 */
                                                    >
                                                        {item.badge}
                                                    </Badge>
                                                )}
                                            </S.NavItemHeader>
                                        </S.NavTextContent>
                                    )}
                                </S.NavContentWrapper>
                            </S.NavButtonContent>
                        </S.NavButton>

                        {/* Tooltip for collapsed state - only on desktop */}
                        {!isExpanded && !isMobile && (
                            <S.Tooltip>
                                <S.TooltipContent>{item.label}</S.TooltipContent>
                            </S.Tooltip>
                        )}
                    </S.NavItemWrapper>
                ))}
            </S.NavigationContainer>

            {/* Footer */}
            <S.Footer>
                <S.FooterContent
                    $isExpanded={
                        isExpanded
                    } /* className={`transition-all duration-500 ${isExpanded ? 'opacity-100' : 'opacity-0'}`} */
                >
                    {isExpanded && (
                        <S.FooterText>
                            <S.FooterPrompt>Need help?</S.FooterPrompt>
                            <S.FooterLinkButton onClick={() => onNavigate('documentation')}>
                                View documentation →
                            </S.FooterLinkButton>
                        </S.FooterText>
                    )}
                </S.FooterContent>
            </S.Footer>
        </S.SideNavContainer>
    )
}

export { type NavItemType } from './SideNav.types'
